/**
 * Reset Password
 *
 * Where the emailed link lands. Sets a new password using the session that link
 * minted — no current password is asked for, because the person here is the one
 * who cannot remember it; opening the mail is what proves who they are.
 *
 * THIS PAGE IS REACHED WITH A SESSION, which makes it the one auth route that
 * must be exempt from App.tsx's "signed in? go to the app" redirect. Without
 * that exemption the redirect fires on arrival and drops the user into the app
 * still not knowing their password, having never seen this form.
 *
 * Succeeding signs every device out, including this one — see
 * completePasswordReset for why — so the end of the flow is the sign-in page,
 * not the dashboard.
 *
 * @module pages/auth/ResetPassword
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Lock, Loader2, AlertCircle } from "lucide-react";
import { completePasswordReset } from "@/config/supabase";
import { logError } from "../../services/logger";
import { YDMWordmark, BrandFooter } from "@/components/brand";
import { Input } from "@/components";

/** Matches the floor GoTrue enforces, so the failure is caught before a round trip. */
const MIN_PASSWORD = 6;

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirm?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const errors: { password?: string; confirm?: string } = {};
    if (password.length < MIN_PASSWORD) errors.password = t("auth.errors.passwordTooShort");
    if (password !== confirm) errors.confirm = t("auth.errors.passwordsDoNotMatch");
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);
    try {
      const result = await completePasswordReset(password, t);
      if (!result.success) {
        // The common case here is an expired or already-used link. GoTrue's own
        // wording says so clearly, so it is passed through rather than replaced.
        setFormError(result.error ?? t("auth.unexpectedError"));
        return;
      }
      // Every session was just revoked, this one included. Sending them to the
      // dashboard would show a page that 401s a moment later.
      navigate("/auth/signin?reset=1", { replace: true });
    } catch (err) {
      setFormError(t("auth.unexpectedError"));
      logError("Auth", "Reset password error", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-500 flex flex-col items-center justify-center p-4 text-ink-950 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-ideas-orange/40 rounded-full blur-2xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-ideas-pink/30 rounded-full blur-2xl" />
      </div>

      <div className="relative max-w-md w-full">
        <div className="text-center mb-8">
          <YDMWordmark variant="hero" bg="yellow" withTagline size="xl" />
          <h1 className="mt-6 font-display italic font-bold text-3xl sm:text-4xl text-ink-950 leading-tight">
            <Trans i18nKey="auth.makeItUnstoppable" />
          </h1>
        </div>

        <div className="bg-white text-ink-900 rounded-2xl shadow-ink border-2 border-ink-950 p-8">
          <h2 className="font-display font-bold text-xl text-ink-950">
            {t("auth.resetPasswordTitle")}
          </h2>
          <p className="mt-2 mb-6 text-sm text-ink-700">{t("auth.resetPasswordBody")}</p>

          {formError && (
            <div role="alert" className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" aria-hidden="true" />
              <span className="text-sm text-error-800">{formError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            <Input
              id="password"
              label={t("auth.newPassword")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.passwordPlaceholder")}
              disabled={isLoading}
              leftIcon={<Lock className="w-5 h-5 text-ink-400" />}
              error={fieldErrors.password}
              aria-invalid={!!fieldErrors.password}
              className="py-3 border-ink-200 focus:border-primary-500 disabled:bg-ink-100"
            />

            <Input
              id="confirm-password"
              label={t("auth.confirmNewPassword")}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={t("auth.passwordPlaceholder")}
              disabled={isLoading}
              leftIcon={<Lock className="w-5 h-5 text-ink-400" />}
              error={fieldErrors.confirm}
              aria-invalid={!!fieldErrors.confirm}
              className="py-3 border-ink-200 focus:border-primary-500 disabled:bg-ink-100"
            />

            {/* Said before they submit, not after: signing every device out is a
                consequence people should not discover by being signed out. */}
            <p className="text-xs text-ink-600">{t("auth.resetSignsOutEverywhere")}</p>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-ink-950 text-primary-500 font-bold uppercase tracking-wide rounded-lg hover:bg-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-ink"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  {t("auth.saving")}
                </>
              ) : (
                t("auth.setNewPassword")
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-700">
            <Link
              to="/auth/signin"
              className="font-bold text-ink-950 hover:text-ink-700 underline underline-offset-2 decoration-2"
            >
              {t("auth.backToSignIn")}
            </Link>
          </p>
        </div>

        <BrandFooter />
      </div>
    </div>
  );
};

export default ResetPassword;
